import React, { useContext, useEffect, useState, useCallback } from 'react';
import { MdRefresh, MdPlayArrow, MdArrowBack } from 'react-icons/md';
import { AppContext } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { fetchInsights, runInsightsAgents, reviveSession } from '../services/backendAPI';
import { AGENTS, ALL_KEYS, pollUntilReady, timeAgo, STALE_AFTER_MS } from '../components/insightsRender';

const AUTORUN_KEY = 'iva_agents_autorun';

// Survives screen navigation so reopening shows the last results instantly while a
// fresh fetch happens in the background ("hold the last item").
let cachedInsights = { sessionId: null, data: null };

const AgentsScreen = ({ onNavigate, onBack }) => {
  const { sessionId, sessionSummary } = useContext(AppContext);
  const { loadedProfileData } = useProfile();
  const [insights, setInsightsState] = useState(() =>
    cachedInsights.sessionId === sessionId ? cachedInsights.data : null
  );
  const [runningKeys, setRunningKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [reviving, setReviving] = useState(false);
  // Set when the server session expired (30-min idle) AND we couldn't auto-rebuild
  // it — e.g. the built-in default profile. Prompts the user to reopen their profile.
  const [expired, setExpired] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [autoRun, setAutoRun] = useState(() => {
    try { return localStorage.getItem(AUTORUN_KEY) === '1'; } catch (_) { return false; }
  });

  // Tick so the per-agent "updated Xm ago" / stale hints stay current.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const setInsights = useCallback((data, sid) => {
    cachedInsights = { sessionId: sid, data };
    setInsightsState(data);
  }, []);

  const toggleAutoRun = () => setAutoRun(v => {
    const next = !v;
    try { localStorage.setItem(AUTORUN_KEY, next ? '1' : '0'); } catch (_) {}
    return next;
  });

  // Re-run only the selected agents. The server keeps every other agent's data
  // in place, so we DON'T blank the screen — we keep showing the current results
  // and only swap in the new payload once the requested agents' timestamps change
  // (the backend bumps agentMeta[key] for each agent it re-runs).
  const runAgents = useCallback(async (keys) => {
    if (!sessionId || !keys.length) return;
    const before = {};
    keys.forEach(k => { before[k] = insights?.agentMeta?.[k]; });
    setRunningKeys(prev => new Set([...prev, ...keys]));
    try {
      let res = await runInsightsAgents(sessionId, keys);
      // Session dropped by the 30-min idle TTL (typically after switching windows).
      // Transparently re-load the profile under the same id, then retry the run.
      if (res?.reason === 'session_not_found') {
        setReviving(true);
        const revived = await reviveSession(sessionId, loadedProfileData);
        setReviving(false);
        if (!revived) { setExpired(true); return; }
        setExpired(false);
        res = await runInsightsAgents(sessionId, keys);
      }
      let data = null;
      for (let i = 0; i < 40; i++) {
        const d = await fetchInsights(sessionId);
        if (d?.ready && keys.every(k => d.agentMeta && d.agentMeta[k] !== before[k])) { data = d; break; }
        await new Promise(r => setTimeout(r, 700));
      }
      if (!data) data = await fetchInsights(sessionId);
      if (data?.ready) setInsights(data, sessionId);
    } finally {
      setRunningKeys(prev => { const next = new Set(prev); keys.forEach(k => next.delete(k)); return next; });
    }
  }, [sessionId, insights, setInsights, loadedProfileData]);

  // On open: SHOW the existing results (the background run already fires on profile
  // load) — don't re-run unless the auto-run toggle is on. This "holds the last item"
  // and shows exactly what IVA saw.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let data = await fetchInsights(sessionId);
      // Session evicted by the 30-min idle TTL (e.g. came back after switching
      // windows). Re-load the profile under the same id — that also re-kicks the
      // background run — so reopening restores results instead of going blank.
      if (data?.reason === 'session_not_found' && !cancelled) {
        setReviving(true);
        const revived = await reviveSession(sessionId, loadedProfileData);
        if (!cancelled) setReviving(false);
        if (!cancelled) setExpired(!revived);
        if (revived && !cancelled) data = await pollUntilReady(sessionId, 12, 700);
      }
      // Catch an initial run that's still in flight (short poll, not a full re-run).
      if (!data?.ready && !cancelled) data = await pollUntilReady(sessionId, 8, 700);
      if (!cancelled && data?.ready) { setInsights(data, sessionId); setExpired(false); }
      if (!cancelled) setLoading(false);
      if (!cancelled && autoRun) runAgents(ALL_KEYS);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const anyRunning = runningKeys.size > 0;
  const ready = !!insights?.ready;
  const profileName = sessionSummary?.customer?.name || loadedProfileData?.name;
  const statusText = reviving ? 'Session expired — reloading profile…'
    : anyRunning ? 'Running…'
    : loading && !ready ? 'Loading latest…'
    : ready ? 'Showing latest results'
    : 'Not run yet';

  return (
    <div className="screen bh-flow agents-screen">
      <header className="bh-flow-hero tall">
        <div className="bh-flow-hero-row">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('insightsScreen'))} aria-label="Back">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Agents</h1>
          </div>
          <button
            onClick={() => runAgents(ALL_KEYS)}
            disabled={!sessionId || anyRunning}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'var(--td-green, #2FBF00)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius-full, 999px)', padding: '8px 16px', fontWeight: 700,
              cursor: (!sessionId || anyRunning) ? 'not-allowed' : 'pointer', opacity: (!sessionId || anyRunning) ? 0.55 : 1,
            }}
          >
            <MdPlayArrow size={18} /> {anyRunning ? 'Running…' : 'Run all'}
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <p className="bh-flow-hero-subtitle" style={{ color: 'var(--bh-black)', margin: 0 }}>
            {profileName ? `${profileName} — ` : ''}{statusText}
          </p>
          {/* Auto-run toggle — when on, the agents re-run every time you open this screen */}
          <button
            onClick={toggleAutoRun}
            title="When on, re-run all agents automatically each time you open this screen"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              background: 'var(--bh-light-gray)', border: 'none', borderRadius: 'var(--radius-full, 999px)',
              padding: '5px 12px 5px 8px', color: 'var(--bh-black)', font: 'var(--bh-caption)', fontWeight: 600,
            }}
          >
            <span style={{
              width: 30, height: 18, borderRadius: 999, flexShrink: 0, position: 'relative',
              background: autoRun ? 'var(--td-green, #2FBF00)' : 'var(--bh-gray)', transition: 'background 0.15s',
            }}>
              <span style={{
                position: 'absolute', top: 2, left: autoRun ? 14 : 2, width: 14, height: 14,
                borderRadius: '50%', background: '#fff', transition: 'left 0.15s',
              }} />
            </span>
            Auto-run on open: {autoRun ? 'On' : 'Off'}
          </button>
        </div>
      </header>

      <div className="bh-flow-body">
        {!sessionId && (
          <div className="bh-form-card" style={{ color: 'var(--bh-gray)' }}>
            Load a profile to run the agents.
          </div>
        )}

        {sessionId && expired && !reviving && (
          <div className="bh-form-card" style={{ background: '#fff4e0', color: '#7a4d00', borderLeft: '3px solid #b07400' }}>
            Your session timed out after 30 minutes idle and couldn’t be restored automatically.
            Reopen your profile from the top bar to refresh the agents.
          </div>
        )}

        {sessionId && AGENTS.map((agent) => {
          const running = runningKeys.has(agent.key);
          const body = ready ? agent.render(insights) : null;
          const hasBody = body != null && (!Array.isArray(body) || body.length > 0);
          const ts = insights?.agentMeta?.[agent.key];
          const ago = timeAgo(ts, now);
          const stale = ts != null && (now - ts) > STALE_AFTER_MS;

          return (
            <div key={agent.key} className="bh-form-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2xs)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xs)', minWidth: 0 }}>
                  <span style={{ fontSize: '1.4rem', flexShrink: 0 }} aria-hidden>{agent.emoji}</span>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>{agent.label}</h3>
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{agent.blurb}</div>
                  </div>
                </div>
                <button
                  onClick={() => runAgents([agent.key])}
                  disabled={anyRunning}
                  title={`Re-run ${agent.label}`}
                  aria-label={`Re-run ${agent.label}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--bh-light-gray)', color: 'var(--bh-black)', border: 'none',
                    cursor: anyRunning ? 'not-allowed' : 'pointer', opacity: anyRunning ? 0.5 : 1,
                  }}
                >
                  <MdRefresh size={16} style={{ animation: running ? 'agent-spin 1s linear infinite' : 'none' }} />
                </button>
              </div>

              <div style={{ marginTop: 'var(--space-2xs)' }}>
                {/* Keep showing the existing result while THIS agent refreshes —
                    only fall back to a placeholder when there's nothing to hold. */}
                {hasBody ? (
                  body
                ) : running ? (
                  <div style={{ color: 'var(--bh-gray)' }}>Running…</div>
                ) : loading && !ready ? (
                  <div style={{ color: 'var(--bh-gray)' }}>Loading latest…</div>
                ) : !ready ? (
                  <div style={{ color: 'var(--bh-gray)' }}>Tap ↻ or “Run all” to analyse.</div>
                ) : (
                  <div style={{ color: 'var(--bh-gray)' }}>Nothing notable found.</div>
                )}

                {ready && (hasBody || ts) && (
                  <div style={{
                    marginTop: 6, fontSize: 11, fontWeight: 600,
                    color: running ? 'var(--bh-gray)' : stale ? '#b07400' : 'var(--bh-gray)',
                  }}>
                    {running
                      ? 'updating…'
                      : ago
                        ? `updated ${ago}${stale ? ' · may be stale' : ''}`
                        : ''}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AgentsScreen;
